import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronDown, Check } from 'lucide-react';
import { useAuthContext } from '../../contexts/AuthContext';
import './FundoTipoSwitcher.css';

interface TipoOpt {
  value: 'liquidos' | 'estruturados';
  label: string;
  permissao: string;
}

const TIPOS: TipoOpt[] = [
  { value: 'liquidos',     label: 'Fundos Líquidos',     permissao: 'VIEW_FUNDS_LIQUIDOS' },
  { value: 'estruturados', label: 'Fundos Estruturados', permissao: 'VIEW_FUNDS' },
];

/**
 * Título da tela de Fundos que também funciona como seletor de contexto.
 * Quando o usuário tem acesso aos DOIS módulos, o título vira um dropdown
 * (navega para /cadastro/fundo/{tipo}); caso contrário é só o texto do título.
 */
const FundoTipoSwitcher: React.FC = () => {
  const { permissions } = useAuthContext();
  const { tipo } = useParams<{ tipo: string }>();
  const navigate = useNavigate();
  const [aberto, setAberto] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  // fecha ao clicar fora
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setAberto(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const atual = TIPOS.find(t => t.value === tipo) ?? TIPOS[0];
  const ambos =
    permissions.includes('VIEW_FUNDS_LIQUIDOS') && permissions.includes('VIEW_FUNDS');

  // só um módulo → título estático (sem dropdown)
  if (!ambos) {
    return <span className="fts-title">{atual.label}</span>;
  }

  const selecionar = (value: TipoOpt['value']) => {
    setAberto(false);
    if (value !== atual.value) navigate(`/cadastro/fundo/${value}`);
  };

  return (
    <span className="fts" ref={ref}>
      <button
        type="button"
        className="fts-trigger"
        onClick={() => setAberto(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={aberto}
      >
        <span className="fts-title">{atual.label}</span>
        <ChevronDown size={20} className={`fts-caret${aberto ? ' fts-caret-open' : ''}`} />
      </button>

      {aberto && (
        <ul className="fts-menu" role="listbox">
          {TIPOS.map(t => (
            <li key={t.value}>
              <button
                type="button"
                role="option"
                aria-selected={t.value === atual.value}
                className={`fts-option${t.value === atual.value ? ' fts-option-active' : ''}`}
                onClick={() => selecionar(t.value)}
              >
                <span>{t.label}</span>
                {t.value === atual.value && <Check size={15} />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </span>
  );
};

export default FundoTipoSwitcher;
